import { Button, Flex, Text } from '@radix-ui/themes'
import { useWallet } from '@solana/wallet-adapter-react'
import { AuthButton } from 'features/auth/components/auth-buttons'
import {
  findProviderWallet,
  getSolanaConnectorProviders,
  isProviderInstalled
} from 'features/auth/utils/get-solana-connector-providers'
import type { SolanaConnectorProvider } from 'features/auth/utils/get-solana-connector-providers'
import { DataTestIDs } from 'shared/constants'
import { RadixText, XyroLoading } from 'shared/ui'
import { ProviderIcon } from './provider-icon'

interface Props {
  isLinking: boolean
  onLink: (provider: SolanaConnectorProvider) => void
}

/**
 * Linking by signature (FE-305) for every wallet app we name (FE-306): an
 * installed wallet links, a missing one links to its download page. The app
 * the user is connected with reads "another account", because linking it
 * again means switching to a second account inside that app first.
 */
export const LinkWalletSection: React.FC<Props> = ({ isLinking, onLink }) => {
  const { wallets, wallet } = useWallet()
  const connectedName = wallet?.adapter.name

  return (
    <Flex
      direction='column'
      gap='3'
    >
      <Text
        size='3'
        weight='bold'
      >
        Link another wallet
      </Text>
      <Text
        size='2'
        color='gray'
      >
        Pick the wallet app and sign the message. To add a second account from
        an app you already use, switch to that account in the app first. A
        wallet can belong to one XYRO account only and cannot be unlinked.
      </Text>

      {getSolanaConnectorProviders().map(provider => {
        const entry = findProviderWallet(wallets, provider)
        const icon = (
          <ProviderIcon
            provider={provider}
            adapterIcon={entry?.adapter.icon}
          />
        )

        if (!isProviderInstalled(wallets, provider)) {
          return (
            <Button
              key={provider.id}
              asChild
              variant='soft'
              color='gray'
              size='3'
              data-testid={DataTestIDs.linkWalletInstall}
            >
              <a
                href={provider.installUrl}
                target='_blank'
                rel='noreferrer'
              >
                Install {provider.name}
              </a>
            </Button>
          )
        }

        return (
          <AuthButton
            key={provider.id}
            disabled={isLinking}
            onClick={() => onLink(provider)}
            dataTestID={DataTestIDs.buttonLinkWallet}
          >
            <XyroLoading loading={isLinking}>
              {icon}
              <RadixText>
                {provider.walletName === connectedName ?
                  `${provider.name} · another account`
                : provider.name}
              </RadixText>
            </XyroLoading>
          </AuthButton>
        )
      })}
    </Flex>
  )
}
